/**
 * Per-organization branding (colours and logo) of the signed-in user's
 * organization, applied with @digitalisation/enlixy-comp's applyBranding().
 *
 * - Source: enlixy-right-admin's public document
 *   `GET /public/branding?org=<alias>&realm=<realm>` → `{name, primary,
 *   secondary, logoUrl, logoPlate, logoSize}` (`{}` when unbranded or
 *   unknown), no login, CORS open. VITE_BRANDING_URL is its origin; in
 *   development without it the request goes to this origin, which the Vite
 *   dev server proxies (vite.config.ts); a deployed build without it asks
 *   nothing and keeps the Focke look.
 * - The package keeps the last branding in localStorage (BRANDING_STORAGE_KEY)
 *   and re-applies it before the first render (initBranding in main.tsx), so a
 *   branded organization never sees the Focke look flash.
 * - Development only: `?branding=<primary>[,<secondary>]` (hex, the "#" may be
 *   left out), with optional `brandingLogo=<url>`, `brandingName=<name>`,
 *   `brandingPlate=1` and `brandingLogoSize=s|m|l`, tries a look without
 *   right-admin; it is kept for later reloads until `?branding=off`.
 */

import { useEffect } from "react";
import {
  applyBranding,
  clearBranding,
  type Branding,
} from "@digitalisation/enlixy-comp";

/** localStorage key of the applied branding (the package's store). */
export const BRANDING_STORAGE_KEY = "performanceCockpit-branding";
/** localStorage key of the development override (dev server only). */
export const DEV_BRANDING_KEY = "performanceCockpit-branding-dev";

/** Origin of the branding document; empty = this origin. */
export const BRANDING_BASE: string = String(
  import.meta.env.VITE_BRANDING_URL ?? "",
).replace(/\/+$/, "");

/** URL of an organization's public branding document. */
export function brandingUrl(
  alias: string,
  realm?: string,
  base: string = BRANDING_BASE,
): string {
  const query = new URLSearchParams({ org: alias });
  if (realm) query.set("realm", realm);
  return `${base}/public/branding?${query.toString()}`;
}

const text = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim() : undefined;

/**
 * The document as a Branding (null: unbranded). A logo path of the document
 * ("/public/branding/logo/…") is resolved against `base`, the host that
 * serves it; applyBranding() drops anything that is not a hex colour or a
 * safe logo URL.
 */
export function toBranding(
  doc: unknown,
  base: string = BRANDING_BASE,
): Branding | null {
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return null;
  const v = doc as Record<string, unknown>;
  let logoUrl = text(v.logoUrl);
  if (logoUrl && base && logoUrl.startsWith("/") && !/^\/[/\\]/.test(logoUrl)) {
    logoUrl = new URL(logoUrl, `${base}/`).href;
  }
  const branding: Branding = {};
  const name = text(v.name);
  const primary = text(v.primary);
  const secondary = text(v.secondary);
  if (name) branding.name = name;
  if (primary) branding.primary = primary;
  if (secondary) branding.secondary = secondary;
  if (logoUrl) branding.logoUrl = logoUrl;
  if (typeof v.logoPlate === "boolean") branding.logoPlate = v.logoPlate;
  if (v.logoSize === "s" || v.logoSize === "m" || v.logoSize === "l") {
    branding.logoSize = v.logoSize;
  }
  return branding.primary || branding.logoUrl ? branding : null;
}

/** Thrown when the document cannot be read (offline, not deployed, an error page). */
export class BrandingUnavailableError extends Error {}

/**
 * Fetches an organization's branding: null when it has none. Throws
 * BrandingUnavailableError when the document cannot be read, so the caller
 * keeps the branding of the last visit instead of falling back to Focke.
 */
export async function fetchBranding(
  alias: string,
  realm: string | undefined,
  signal?: AbortSignal,
): Promise<Branding | null> {
  let res: Response;
  try {
    res = await fetch(brandingUrl(alias, realm), {
      signal,
      credentials: "omit",
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new BrandingUnavailableError("branding: request failed");
  }
  if (!res.ok) {
    throw new BrandingUnavailableError(`branding: HTTP ${res.status}`);
  }
  // A page instead of JSON: the route is not served there (yet).
  if (!(res.headers.get("content-type") ?? "").includes("application/json")) {
    throw new BrandingUnavailableError("branding: not a JSON answer");
  }
  return toBranding(await res.json());
}

const hex = (v: string | null): string | undefined => {
  const s = (v ?? "").trim().replace(/^#/, "");
  return /^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(s) ? `#${s}` : undefined;
};

/**
 * The development override: a Branding, null for "Focke" (`?branding=off`),
 * undefined when there is none. A `branding` parameter is stored for later
 * reloads; `off` removes it.
 */
export function devBrandingOverride(
  search: string,
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
): Branding | null | undefined {
  const params = new URLSearchParams(search);
  const value = params.get("branding");
  try {
    if (value === null) {
      const stored = storage.getItem(DEV_BRANDING_KEY);
      return stored ? (JSON.parse(stored) as Branding) : undefined;
    }
    if (value.trim().toLowerCase() === "off") {
      storage.removeItem(DEV_BRANDING_KEY);
      return null;
    }
  } catch {
    return undefined;
  }
  const [primary, secondary] = value.split(",").map(hex);
  const size = params.get("brandingLogoSize");
  const branding: Branding = {
    primary,
    secondary,
    logoUrl: params.get("brandingLogo") ?? undefined,
    name: params.get("brandingName") ?? undefined,
    logoPlate: params.get("brandingPlate") === "1" || undefined,
    logoSize: size === "s" || size === "m" || size === "l" ? size : undefined,
  };
  for (const k of Object.keys(branding) as (keyof Branding)[]) {
    if (branding[k] === undefined) delete branding[k];
  }
  try {
    storage.setItem(DEV_BRANDING_KEY, JSON.stringify(branding));
  } catch {
    // private mode: the override applies to this page only
  }
  return branding;
}

/**
 * Applies the branding of `alias` (the organization of the signed-in user)
 * and follows changes of it. Without an organization the Focke look applies.
 */
export function useOrgBranding(alias: string | null, realm?: string): void {
  useEffect(() => {
    if (import.meta.env.DEV) {
      const dev = devBrandingOverride(window.location.search, localStorage);
      if (dev !== undefined) {
        if (dev) applyBranding(dev);
        else clearBranding();
        return;
      }
    }
    if (!alias) {
      clearBranding();
      return;
    }
    // Deployed builds ask only once VITE_BRANDING_URL names the document's host.
    if (!BRANDING_BASE && !import.meta.env.DEV) return;
    const controller = new AbortController();
    fetchBranding(alias, realm, controller.signal)
      .then((branding) => {
        if (controller.signal.aborted) return;
        if (branding) applyBranding(branding);
        else clearBranding();
      })
      .catch((e: unknown) => {
        // unavailable: keep the branding of the last visit
        if (!controller.signal.aborted) console.warn(String(e));
      });
    return () => controller.abort();
  }, [alias, realm]);
}
